import { useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";

import { getErrorMessage } from "@/core/services";
import { useAsyncList, useLastKnownTotal, useStableValue, useTableState } from "@/ds";
import type { FilterValue } from "@/ds";
import { DEFAULT_USERS_SORT, isUsersSortField, listUsers } from "@/modules/users";
import type { User, UsersSort, UsersSortField } from "@/modules/users";
import {
  PERFIL_FILTER_KEY,
  TIME_FILTER_KEY,
  USERS_ERROR_MESSAGE,
  USERS_FORBIDDEN_MESSAGE,
} from "../users.constants";

/** Critérios da linha de filtros: cada um aceita vários valores, e vazio não restringe a consulta. */
export interface UsersFilters {
  readonly time: string[];
  readonly papelId: string[];
}

const NO_FILTERS: UsersFilters = { time: [], papelId: [] };

interface ListaParams {
  busca: string;
  filters: UsersFilters;
  page: number;
  pageSize: number;
  sort: UsersSort;
}

/** Model da listagem: busca, filtros, página e ordenação no servidor; mudar um critério volta à página 1. */
export function useUsersLista() {
  const [searchParams] = useSearchParams();
  const [search, setSearch] = useState("");
  // O Perfis leva à lista já filtrada pelo perfil (`?papelId=`); o valor entra só na montagem.
  const [rawFilters, setFilters] = useState<UsersFilters>(() => {
    const papelId = searchParams.get("papelId")?.trim();
    return { ...NO_FILTERS, papelId: papelId ? [papelId] : [] };
  });
  const filters = useStableValue(rawFilters);
  const busca = search.trim();
  // O total de sem perfil acompanha a consulta, mas a lista só entrega itens e total.
  const [totalSemPerfil, setTotalSemPerfil] = useState<number | null>(null);

  const { lastTotal, remember } = useLastKnownTotal();
  const tabela = useTableState<UsersSortField>({
    isSortField: isUsersSortField,
    total: lastTotal,
    resetKey: JSON.stringify([busca, filters]),
  });
  // Sem coluna escolhida a consulta segue a ordem padrão (Nome A–Z) e nenhum cabeçalho mostra a seta.
  const appliedSort: UsersSort = tabela.sort ?? DEFAULT_USERS_SORT;

  const lista = useAsyncList<ListaParams, User>({
    fetcher: async (params, signal) => {
      const {
        users,
        total,
        totalSemPerfil: semPerfil,
      } = await listUsers({
        pesquisa: params.busca,
        time: params.filters.time,
        papelId: params.filters.papelId,
        page: params.page,
        pageSize: params.pageSize,
        sort: params.sort,
        signal,
      });
      if (!signal.aborted) setTotalSemPerfil(semPerfil);
      return { items: users, total };
    },
    params: { busca, filters, page: tabela.page + 1, pageSize: tabela.rows, sort: appliedSort },
    debounceKey: busca,
    forbiddenMessage: USERS_FORBIDDEN_MESSAGE,
    errorMessage: USERS_ERROR_MESSAGE,
    resolveErrorMessage: getErrorMessage,
  });
  remember(lista.status, lista.total);

  const filterValue = useMemo<FilterValue>(
    () => ({
      [TIME_FILTER_KEY]: filters.time,
      [PERFIL_FILTER_KEY]: filters.papelId,
    }),
    [filters],
  );

  return {
    search,
    busca,
    filters,
    filterValue,
    appliedSort,
    hasSearch: busca !== "",
    hasFilters: filters.time.length > 0 || filters.papelId.length > 0,
    items: lista.items,
    total: lista.total,
    totalSemPerfil,
    status: lista.status,
    errorMessage: lista.errorMessage,
    reload: lista.reload,
    tabela,
    /** Sem espaço no começo (a busca não nasce de um espaço) e sem refazer o estado quando nada mudou. */
    buscar: (texto: string) => {
      const proximo = texto.trimStart();
      if (proximo !== search) setSearch(proximo);
    },
    clearSearch: () => setSearch(""),
    /** Os critérios da linha de filtros entram em conjunto, com todos os valores escolhidos em cada campo. */
    applyFilters: (value: FilterValue) =>
      setFilters({
        time: value[TIME_FILTER_KEY] ?? [],
        papelId: value[PERFIL_FILTER_KEY] ?? [],
      }),
    /** Devolve a consulta sem critério de filtragem; a busca permanece. */
    clearFilters: () => setFilters(NO_FILTERS),
  };
}
